import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Loader } from '@opendoor/atoms/loader';
import { AccountForm, AppleAuthRedirectButton } from '@opendoor/auth-fe/forms';
import { analytics, globalObservability } from '@opendoor/observability/slim';
import Cookies from 'js-cookie';

import { getLoggedInRedirectUrl } from '../../helpers/accountLoggedInRedirect';
import {
  clearMagicLinkAccountSession,
  isMagicLinkAccountSession,
} from '../../helpers/magicLinkAccountSession';
import * as api from '../api';
import { useTimeBoundedFeatureFlagEvaluator } from '../helpers/hooks';
import { AccountMagicLinkGate } from './AccountMagicLinkGate';
import { buildLoginSmsConsentInput, LOGIN_SMS_CONSENT } from './loginSmsConsent';
import { useAccountFormSession } from './useAccountFormSession';

const Sentry = globalObservability.getSentryClient();

const UNAUTHORIZED_STATUS_CODE = 401;

// Upper bound on how long the post-login redirect is held for the SMS-consent
// write before proceeding regardless.
const CONSENT_WRITE_TIMEOUT_MS = 5000;

interface IAccountFormPageProps {
  config?: 'resaleClosingDashboard';
  redirectTo?: string | undefined;
  emailPrefill?: string | undefined;
  updatedPassword?: boolean;
}

const AccountFormPage: React.FC<IAccountFormPageProps> = ({
  config,
  emailPrefill,
  redirectTo,
  updatedPassword,
}) => {
  const {
    logIn,
    register,
    requestHumanStatus,
    hasGeneratedPassword,
    isNewHuman,
    loginRequestStatus,
    humanStatusRequestStatus,
    registrationRequestStatus,
    email,
    requestResetPassword,
    maybeFetchSessionInfo,
    loggedIn,
    serverLoginRedirect,
    human,
    infoIsLoaded,
  } = useAccountFormSession();
  const evaluateAccountFormFlag = useTimeBoundedFeatureFlagEvaluator();
  const getSignInText = (config?: string) => {
    let loginHeaderText: string | undefined;
    let registrationHeaderText: string | undefined;
    switch (config) {
      case 'resaleClosingDashboard':
        loginHeaderText = 'Sign in to get back to your dashboard';
        registrationHeaderText = 'Sign up to get access to your dashboard';
        break;

      default:
        break;
    }

    return { loginHeaderText, registrationHeaderText };
  };

  const { loginHeaderText, registrationHeaderText } = getSignInText(config);

  // SMS-consent intent captured from the registration checkbox. `undefined`
  // means no registration is in flight (e.g. the login path), so the redirect
  // is never held.
  const pendingSmsConsentRef = useRef<boolean | undefined>(undefined);
  const consentInFlightRef = useRef(false);
  const [consentSettled, setConsentSettled] = useState(false);

  const redirectIfLoggedIn = useCallback(() => {
    if (!loggedIn || isMagicLinkAccountSession()) {
      return;
    }

    // Hold the redirect until a pending SMS-consent write settles so the
    // request isn't cancelled by navigation.
    if (pendingSmsConsentRef.current !== undefined && !consentSettled) {
      return;
    }

    analytics.trackEvent('login', 'redirect', undefined, {
      email,
      config,
      redirectTo,
      serverLoginRedirect,
    });

    window.location.assign(getLoggedInRedirectUrl(redirectTo, serverLoginRedirect));
  }, [config, email, loggedIn, redirectTo, serverLoginRedirect, consentSettled]);

  // After a registration where the SMS-consent checkbox was shown, record the
  // new customer's marketing-SMS preference before the redirect — checked opts
  // in, unchecked opts out.
  useEffect(() => {
    const subscribed = pendingSmsConsentRef.current;
    if (!loggedIn || subscribed === undefined || consentSettled || consentInFlightRef.current) {
      return;
    }
    consentInFlightRef.current = true;
    let cancelled = false;
    // Skip the state update if this effect run was cleaned up (e.g. unmount)
    // while the request was in flight.
    const settle = () => {
      if (!cancelled) {
        setConsentSettled(true);
      }
    };
    // Watchdog: the redirect is held until consent settles, so never let a slow
    // or hung Athena call block login indefinitely — proceed after a deadline.
    const watchdog = setTimeout(settle, CONSENT_WRITE_TIMEOUT_MS);
    void (async () => {
      try {
        const customerUuid = await api.getCurrentCustomerUuid();
        if (customerUuid) {
          await api.upsertCommPreference(buildLoginSmsConsentInput(customerUuid, subscribed));
        } else {
          // Surface the silent consent-drop instead of skipping quietly.
          Sentry.captureException?.(
            new Error('login SMS consent: missing current customer UUID; preference not written'),
          );
        }
      } finally {
        clearTimeout(watchdog);
        settle();
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(watchdog);
    };
  }, [loggedIn, consentSettled]);

  const completeMagicLinkGate = useCallback(() => {
    clearMagicLinkAccountSession();
    redirectIfLoggedIn();
  }, [redirectIfLoggedIn]);

  const wasLoggedIn = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    analytics.register({ login: Cookies.get('login-test') || 'no-cookie' });
    analytics.trackEvent('login', 'load', undefined, {
      email,
      config,
      redirectTo,
      emailPrefill,
    });
    maybeFetchSessionInfo();
  }, []);

  useEffect(() => {
    if (wasLoggedIn.current === true && !loggedIn) {
      clearMagicLinkAccountSession();
    }
    wasLoggedIn.current = loggedIn;
    if (loggedIn) {
      redirectIfLoggedIn();
    }
  }, [loggedIn, loginRequestStatus, redirectIfLoggedIn]);

  useEffect(() => {
    if (
      loginRequestStatus &&
      loginRequestStatus.error &&
      loginRequestStatus.statusCode !== UNAUTHORIZED_STATUS_CODE
    ) {
      Sentry.captureException?.(loginRequestStatus.error);
    }
  }, [loginRequestStatus]);

  const trackLoginEvent = (
    topic: 'google-auth' | 'one-time-password',
    label: string,
    extra: Record<string, unknown>,
  ) => {
    analytics.trackEvent('login', topic, label, extra);
  };

  const handlePasswordlessLoginRequest = async (email: string) => {
    trackLoginEvent('one-time-password', 'request', { email, redirectTo });
    const response = await api.requestNewTokenByEmail(email, redirectTo);
    if (!response || !response.data || response.errors) {
      const errors = response && response.errors;
      trackLoginEvent('one-time-password', 'error', { email, errors });
      return { success: false, errors };
    }

    trackLoginEvent('one-time-password', 'success', { email });
    return { success: true };
  };

  let headerText = loginHeaderText;

  if (updatedPassword) {
    headerText = 'Your password was successfully changed. Please sign in.';
  }
  const onRegister = (
    email?: string,
    name?: string,
    password?: string,
    phone?: string,
    googleIDToken?: string,
  ) => {
    register(email, name, password, phone, googleIDToken);
    analytics.trackEvent('session', 'register', 'account-creation', { email });
  };

  if (loggedIn && isMagicLinkAccountSession()) {
    return (
      <Col className="w-full items-center">
        <Col className="w-full max-w-120 items-center justify-center px-6 py-12 lg:px-0 lg:py-18">
          <AccountMagicLinkGate
            human={human}
            infoIsLoaded={infoIsLoaded}
            hasGeneratedPassword={hasGeneratedPassword}
            humanStatusRequestStatus={humanStatusRequestStatus}
            requestHumanStatus={requestHumanStatus}
            requestResetPassword={requestResetPassword}
            onComplete={completeMagicLinkGate}
          />
        </Col>
      </Col>
    );
  }

  return (
    <Col className="w-full items-center justify-center px-6 py-12 lg:px-0 lg:py-18">
      <AccountForm
        featureFlagProps={{
          evaluate: evaluateAccountFormFlag,
          loading: <Loader size="3xl" />,
        }}
        accountFormProps={{
          loginAnalyticsPrefix: 'cosmos-login',
          analyticsName: 'cosmos-account-form',
          loginHeaderText: headerText,
          registrationHeaderText: registrationHeaderText,
          email: email || emailPrefill,
          hasGeneratedPassword: hasGeneratedPassword,
          error:
            loginRequestStatus.error ||
            humanStatusRequestStatus.error ||
            registrationRequestStatus.error,
          passwordLoading:
            !!serverLoginRedirect ||
            loginRequestStatus.isLoading ||
            registrationRequestStatus.isLoading,
          emailLoading: humanStatusRequestStatus.isLoading,
          isNewHuman: isNewHuman,
          onLogin: logIn,
          overrideHasSubmittedEmailCheckForLogin: !!emailPrefill,
          onRegister: onRegister,
          onHumanStatusRequest: requestHumanStatus,
          requestResetPassword,
          onPasswordlessLoginRequest: (email: string) => handlePasswordlessLoginRequest(email),
          showGoogleAuth: true,
          loginRedirectTo: redirectTo,
          smsConsentCheckboxes: LOGIN_SMS_CONSENT.checkboxes,
          onSmsConsentChange: (subscribed: boolean) => {
            pendingSmsConsentRef.current = subscribed;
          },
          renderAppleAuth: () => (
            <Col className="items-center justify-center pt-6">
              <AppleAuthRedirectButton
                analyticsPrefix="cosmos-login"
                variant="secondaryInverse"
                redirectTo={redirectTo}
              />
            </Col>
          ),
        }}
      />
    </Col>
  );
};

export default AccountFormPage;
